import _ from 'underscore';
import { startsWith, endsWith } from 'underscore.string';

function flattenAndOperators(conditions) {
  const result = [];
  _.each(conditions, (con) => {
    const condition = con;
    if (condition.$and) {
      result.push(...(condition.$and || []));
      delete condition.$and;
    }
    if (!_.isEmpty(condition)) {
      result.push(condition);
    }
  });
  return result;
}

function isFieldName(expressionKey) {
  return expressionKey && expressionKey[0] !== '$';
}

function toExplicitCondition(field, originalOperator, originalValue) {
  let operator = originalOperator;
  let value = originalValue;

  if (operator === '$regex') {
    if (startsWith(value, '.*') && endsWith(value, '.*')) {
      operator = '$contains';
      value = value.substr(2, value.length - 4);
    } else if (startsWith(value, '.*')) {
      operator = '$endswith';
      value = value.substr(2, value.length - 2);
    } else if (endsWith(value, '.*')) {
      operator = '$startswith';
      value = value.substr(0, value.length - 2);
    }
  }

  return { [field]: { [operator]: value } };
}

function getFieldExpressions(field, expression) {
  if (_.isObject(expression)) {
    return _.map(expression, (value, operator) =>
      toExplicitCondition(field, operator, value));
  }
  return [toExplicitCondition(field, '$eq', expression)];
}

function bfsNextLevel(filters) {
  const nextLevel = [];
  _.each(filters, ({ $and = [], $or = [] }) => nextLevel.push(...$and, ...$or));
  return nextLevel;
}

/**
 * Normalize raw filter expression.
 * @param {Object} filter - raw filter expression.
 * @returns {Object} - normalized filter object.
 */
export function normalizeFilter(filter) {
  let conditions = [];
  _.each(filter, (expression, expressionKey) => {
    if (isFieldName(expressionKey)) {
      conditions.push(...getFieldExpressions(expressionKey, expression));
    } else if (_.isArray(expression) && expression.length) {
      if (expressionKey === '$and') {
        conditions.push(..._.map(expression, normalizeFilter));
      } else if (expressionKey === '$or') {
        if (expression.length === 1) {
          conditions.push(normalizeFilter(expression[0]));
        } else {
          conditions.push({ $or: _.map(expression, normalizeFilter) });
        }
      }
    }
  });
  conditions = flattenAndOperators(conditions);
  if (!conditions.length) {
    return {};
  }
  if (conditions.length === 1) {
    return conditions[0];
  }
  return { $and: conditions };
}

/**
 * Calculate normalized filter's depth.
 * @param {Object} filter - normalized filter object.
 * @returns {number} - filter object's depth.
 */
export function calculateDepth(filter) {
  if (_.isEmpty(filter)) {
    return 0;
  }
  let depth = 0;
  let filters = [filter];
  while (!_.isEmpty(filters)) {
    filters = bfsNextLevel(filters);
    depth += 1;
  }
  return depth;
}
